import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { HealthScore } from '@/components/common/HealthScore';
import { IngredientTag } from '@/components/common/IngredientTag';
import { getScanHistory } from '@/lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  AlertTriangle, 
  Eye, 
  Calendar,
  Share2,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Barcode
} from 'lucide-react';
import { format } from 'date-fns';

const AnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        // First try to get from scan history
        const history = await getScanHistory();
        const found = history.find(item => item.id === id);
        if (found) {
          setAnalysis(found);
        } else {
          // Analysis not in history - might have just been scanned
          // Show error but don't redirect immediately
          setError('Analysis not found in history. It may still be processing.');
          toast.error('Analysis not found');
        }
      } catch (error) {
        console.error('Failed to fetch analysis:', error);
        setError('Failed to load analysis. Please try scanning again.');
        toast.error('Failed to load analysis');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAnalysis();
    } else {
      setError('No analysis ID provided');
      setLoading(false);
    }
  }, [id]);

  // Group ingredients by health impact
  const groupedIngredients = analysis?.ingredients_analyzed?.reduce((acc, ing) => {
    const impact = ing.health_impact?.toLowerCase() || 'other';
    if (!acc[impact]) acc[impact] = [];
    acc[impact].push(ing);
    return acc;
  }, {}) || {};

  // Get score description
  const getScoreDescription = (score) => {
    if (score === null || score === undefined) return 'Health score unavailable - no ingredient data to analyze.';
    if (score >= 80) return 'This product has a healthy ingredient profile with mostly natural components.';
    if (score >= 60) return 'This product has a decent ingredient profile but contains some processed components.';
    if (score >= 40) return 'This product contains several processed ingredients. Consider healthier alternatives.';
    if (score >= 20) return 'This product has many concerning ingredients. Frequent consumption is not recommended.';
    return 'This product contains multiple harmful ingredients. Consider avoiding it.';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Analysis Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {error || 'The analysis you\'re looking for could not be found.'}
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/scan">
              <Button className="bg-[#5DBDB3] hover:bg-[#4DA9A0]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Scan Again
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="outline">
                View History
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="analysis-page">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link 
          to="/history" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-history"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to History
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" data-testid="product-name">
              {analysis.product_name}
            </h1>
            
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {analysis.barcode && (
                <div className="flex items-center gap-1">
                  <Barcode className="w-4 h-4" />
                  {analysis.barcode}
                </div>
              )}
              {(analysis.analyzed_at || analysis.created_at) && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(analysis.analyzed_at || analysis.created_at), 'MMM d, yyyy h:mm a')}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Summary */}
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Analysis Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {analysis.ai_analysis && !analysis.ai_analysis.includes('could not be completed') ? (
                  <p className="text-muted-foreground leading-relaxed" data-testid="analysis-summary">
                    {analysis.ai_analysis}
                  </p>
                ) : analysis.ingredients_raw ? (
                  <p className="text-muted-foreground leading-relaxed" data-testid="analysis-summary">
                    This product contains: {analysis.ingredients_raw}
                  </p>
                ) : (
                  <div className="text-center py-4">
                    <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">
                      No ingredient data available for this product.
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      This product was found but doesn't have ingredient information in our database.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Allergen Warnings */}
            {(analysis.allergens_detected?.length > 0 || analysis.allergen_warnings?.length > 0) && (
              <Card className="border border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Allergen Warnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2" data-testid="allergen-warnings">
                    {(analysis.allergens_detected || analysis.allergen_warnings || []).map((allergen, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="bg-red-100 text-red-700 border-red-300 py-1.5 px-3"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {typeof allergen === 'string' ? allergen : allergen.name || allergen}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hidden Ingredients */}
            {analysis.hidden_ingredients?.length > 0 && (
              <Card className="border border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700">
                    <Eye className="w-5 h-5" />
                    Hidden Ingredients Detected
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-purple-600 mb-3">
                    These ingredients may be disguised under other names:
                  </p>
                  <div className="space-y-3" data-testid="hidden-ingredients">
                    {analysis.hidden_ingredients.map((ingredient, index) => {
                      // Handle both string format and object format
                      const name = typeof ingredient === 'string' ? ingredient : ingredient.name;
                      const disguisedAs = typeof ingredient === 'object' ? ingredient.disguised_as : null;
                      const concerns = typeof ingredient === 'object' ? ingredient.concerns : null;
                      const severity = typeof ingredient === 'object' ? ingredient.severity : null;
                      
                      return (
                        <div 
                          key={index}
                          className={`p-3 rounded-lg border ${
                            severity === 'high' ? 'bg-red-50 border-red-200' :
                            severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                            'bg-purple-50 border-purple-200'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <Eye className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              severity === 'high' ? 'text-red-600' :
                              severity === 'medium' ? 'text-amber-600' :
                              'text-purple-600'
                            }`} />
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${
                                severity === 'high' ? 'text-red-700' :
                                severity === 'medium' ? 'text-amber-700' :
                                'text-purple-700'
                              }`}>
                                {name}
                              </p>
                              {disguisedAs && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Listed as: {disguisedAs}
                                </p>
                              )}
                              {concerns && Array.isArray(concerns) && concerns.length > 0 && (
                                <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                                  {concerns.slice(0, 2).map((concern, i) => (
                                    <li key={i}>{concern}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            {severity && (
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  severity === 'high' ? 'bg-red-100 text-red-700 border-red-300' :
                                  severity === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                  'bg-green-100 text-green-700 border-green-300'
                                }`}
                              >
                                {severity}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ingredients by Category */}
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Ingredient Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Check if there are any ingredients to display */}
                {(!groupedIngredients.safe?.length && !groupedIngredients.caution?.length && !groupedIngredients.avoid?.length && !groupedIngredients.other?.length) ? (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">
                      No ingredient analysis available.
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You can contribute ingredient data for this product to help others.
                    </p>
                    <Link to="/scan">
                      <Button variant="outline" size="sm" className="mt-3">
                        Contribute Product Data
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    {/* Safe Ingredients */}
                    {groupedIngredients.safe?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium text-sm">Safe Ingredients</span>
                          <Badge variant="secondary" className="ml-auto">
                            {groupedIngredients.safe.length}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="safe-ingredients">
                          {groupedIngredients.safe.map((ing, index) => (
                            <IngredientTag key={index} ingredient={ing} />
                          ))}
                        </div>
                      </div>
                    )}

                    {groupedIngredients.safe?.length > 0 && (groupedIngredients.caution?.length > 0 || groupedIngredients.avoid?.length > 0) && (
                      <Separator />
                    )}

                    {/* Caution Ingredients */}
                    {groupedIngredients.caution?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          <span className="font-medium text-sm">Use Caution</span>
                          <Badge variant="secondary" className="ml-auto">
                            {groupedIngredients.caution.length}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="caution-ingredients">
                          {groupedIngredients.caution.map((ing, index) => (
                            <IngredientTag key={index} ingredient={ing} />
                          ))}
                        </div>
                      </div>
                    )}

                    {groupedIngredients.caution?.length > 0 && groupedIngredients.avoid?.length > 0 && (
                      <Separator />
                    )}

                    {/* Avoid Ingredients */}
                    {groupedIngredients.avoid?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="font-medium text-sm">Best to Avoid</span>
                          <Badge variant="secondary" className="ml-auto">
                            {groupedIngredients.avoid.length}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2" data-testid="avoid-ingredients">
                          {groupedIngredients.avoid.map((ing, index) => (
                            <IngredientTag key={index} ingredient={ing} />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Raw Ingredients */}
            {analysis.ingredients_raw && (
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle>Original Ingredient List</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground font-mono bg-muted p-4 rounded-lg" data-testid="raw-ingredients">
                    {analysis.ingredients_raw}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Health Score */}
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Health Score</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <HealthScore score={analysis.health_score} size="lg" />
                <p className="text-sm text-muted-foreground text-center mt-4">
                  {getScoreDescription(analysis.health_score)}
                </p>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border border-border">
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Ingredients</span>
                  <Badge variant="secondary">{analysis.ingredients_analyzed?.length || 0}</Badge>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Safe</span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {groupedIngredients.safe?.length || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Caution</span>
                  <Badge className="bg-amber-100 text-amber-700">
                    {groupedIngredients.caution?.length || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avoid</span>
                  <Badge className="bg-red-100 text-red-700">
                    {groupedIngredients.avoid?.length || 0}
                  </Badge>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Hidden Ingredients</span>
                  <Badge className="bg-purple-100 text-purple-700">
                    {analysis.hidden_ingredients?.length || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="border border-border">
              <CardContent className="pt-6 space-y-3">
                <Link to="/scan" className="block">
                  <Button className="w-full" data-testid="scan-another-btn">
                    Scan Another Product
                  </Button>
                </Link>
                <Link to="/community" className="block">
                  <Button variant="outline" className="w-full">
                    Report Hidden Ingredient
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalysisPage;
